import React from "react";
import customizeProgramCard from "../assets/img/customize-your-program-card.jpeg";

/* ------------------------------------------------------------------
   IPDI Program Cards
  
------------------------------------------------------------------- */

const programs = [
  {
    image:
      "https://images.unsplash.com/photo-1487958449943-2429e8be8625?auto=format&fit=crop&w=800&q=80",
    eyebrow: "Flagship Program",
    category: "100% Placement Assured",
    title: "Executive Architect Program",
    meta: ["6 Months", "Live interactions & coaching"],
    nextBatch: "Aug 2026",
    valueProp:
      "Become an industry-ready architect through live projects, BIM workflows, Architecture Services and placement support.",
    highlights: [
      "National + Intl. Expert Trainers",
      "Live Project Enrolment",
      "BIM Architecture + AI Render Tools",
        "Tailored Learning Path",
    ],
    audience: ["B.Arch Graduates", "Architect Trainees"],
    ctaLabel: "View Program",
    link: "/course-executive-architect",
  },
  {
    image:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=700&q=75&quot;",
    badge: "100% Placement Assured",
    eyebrow: "Flagship Program",
    title: "Executive Interior Designer",
    meta: ["5 Months", "Live interactions & coaching", "15 hrs/week"],
    nextBatch: "Aug 2026",
    valueProp:
      "We build the path from imagination to execution and open doors to become part of world-renowned organizations.",
    highlights: [
      "National + Intl. Expert Trainers",
      "Live Project Enrolment",
      "250 Hours Live Sessions",
      "100% Placement Assured",
    ],
    audience: ["B.Des Graduates", "Design Aspirants"],
    ctaLabel: "View Program",
    link: "/course-executive-interior",
  },
  {
    image: customizeProgramCard,
    badge: "100% Placement Assured",
    eyebrow: "Build Your Own",
    title: "Customize Your Program",
    meta: ["As Per Module", "Live interactions & coaching"],
    nextBatch: "Aug 2026",
    valueProp:
      "Unlock your potential. Upgrade your skills and become deserving of the next position — a training path designed around you.",
    highlights: [
      "Choose From 15+ Modules",
      "100% Placement Assured",
      "Career Counselling Included",
      "National + Intl. Expert Trainers",
    ],
    audience: ["Career Switchers", "B.Arch+1yr Exp."],
    ctaLabel: "Start Application",
    link: "/customize-your-program",
  },
];

/* ---------------- Icons (inline, no external deps) ---------------- */

const IconClock = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);
const IconVideo = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </svg>
);
const IconTarget = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v6m0 10v6m11-11h-6M7 12H1" />
  </svg>
);
const IconStar = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M12 2l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 9l6-1z" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const IconBadge = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M12 2l9 4.5v11L12 22l-9-4.5v-11L12 2z" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
  </svg>
);
const IconUsers = () => (
  <svg viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth="3">
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2 21c0-3.6 3-6 7-6s7 2.4 7 6" />
    <path d="M16 5.2c1.6.3 2.8 1.7 2.8 3.3 0 1.6-1.2 3-2.8 3.3" />
    <path d="M19 15.3c1.9.5 3 1.9 3 3.7" />
  </svg>
);

const highlightIcons = [IconTarget, IconVideo, IconStar, IconCheck];

/* ---------------- Meta icon helper ----------------
   Meta chips (other than the first one, which is always the duration)
   are picked by CONTENT, not by array position. This guarantees the
   same label (e.g. "Live interactions & coaching") always gets the
   same icon across every card, regardless of whether it's meta[1] or
   meta[2] in a given program's data.
------------------------------------------------------------------- */
function getMetaIcon(text) {
  if (!text) return IconClock;
  if (text.includes("Live interactions")) return IconVideo;
  if (text.includes("hrs/week")) return IconUsers;
  return IconClock;
}

/* ---------------- Small vertical card (light theme) ----------------
   Uses ORIGINAL class names: ipdi-title, ipdi-meta-row, ipdi-value-prop,
   ipdi-highlight-grid, ipdi-audience-cta-row, ipdi-cta-primary, etc.
   Only used by ProgramCard now. Safe to edit.
------------------------------------------------------------------- */

function ProgramCard({ program }) {
  return (
    <div className="ipdi-card ipdi-card--light" id="programs">
      <div className="ipdi-card-image">
        <img src={program.image} alt={program.title} loading="lazy" />
        <div className="ipdi-eyebrow-badge">
          <IconBadge />
          {program.eyebrow}
        </div>
      </div>

      <div className="ipdi-card-body-wrap">
        <div className="ipdi-card-body">
          {/* Top content block: everything above the footer row lives here.
             The footer row below is pinned to the bottom via
             justify-content:space-between on .ipdi-card-body, so no matter
             how much this top block grows/shrinks, the footer stays put. */}
          <div className="ipdi-card-top">
            <h2 className="ipdi-title">{program.title}</h2>

            <div className="ipdi-meta-row ipdi-meta-row--footer">
              <span className="ipdi-meta-chip ipdi-meta-chip--muted">
                <IconClock />
                {program.meta[0]}
              </span>
              {program.meta[1] && (
                <span className="ipdi-meta-chip ipdi-meta-chip--muted">
                  {React.createElement(getMetaIcon(program.meta[1]))}
                  {program.meta[1]}
                </span>
              )}
              {program.meta[2] && (
                <span className="ipdi-meta-chip ipdi-meta-chip--muted">
                  {React.createElement(getMetaIcon(program.meta[2]))}
                  {program.meta[2]}
                </span>
              )}
              {program.nextBatch && (
                <span className="ipdi-meta-chip ipdi-meta-chip--muted">
                  <IconUser />
                  Next Batch: {program.nextBatch}
                </span>
              )}
              {program.placementNote && (
                <span className="ipdi-meta-chip ipdi-meta-chip--muted">
                  <IconBadge />
                  {program.placementNote}
                </span>
              )}
            </div>

            <p className="ipdi-value-prop">{program.valueProp}</p>

            <div className="ipdi-highlight-grid">
              {program.highlights.map((h, i) => {
                const Icon = highlightIcons[i % highlightIcons.length];
                return (
                  <div className="ipdi-highlight-item" key={h}>
                    <Icon />
                    <span>{h}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ipdi-audience-cta-row">
            <div className="ipdi-audience-block">
              <div className="ipdi-audience-label">Built For</div>
              <div className="ipdi-audience-row">{program.audience.join(" | ")}</div>
            </div>

            <a className="ipdi-cta-primary" href={program.link}>
              {program.ctaLabel}
              <IconArrow />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Third card: Our Promise ----------------
   Uses NEW, UNIQUE class names prefixed "ipdi-promise-": ipdi-card-promise,
   ipdi-promise-image, ipdi-promise-badge, ipdi-promise-title,
   ipdi-promise-value, ipdi-promise-highlight-grid, ipdi-promise-footer-row,
   etc.
   These do NOT share CSS with ProgramCard anymore — edit freely in the
   "PROMISE CARD CSS" section below without touching the other 2 cards.

   NOTE: top content (percent/title/value/highlights) is wrapped in
   ipdi-promise-top so the footer row can be pinned to the bottom via
   justify-content:space-between — this keeps top/bottom padding equal
   to the other two cards even though this card has less content.
------------------------------------------------------------------- */

function ProgramCardPromise() {
  return (
    <div className="ipdi-card-promise" id="programs">
      <div className="ipdi-promise-image">
        <img
          src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
          alt="Students collaborating on a project"
          loading="lazy"
        />
        <div className="ipdi-promise-badge">
          <IconBadge />
          Our Promise
        </div>
      </div>

      <div className="ipdi-promise-body-wrap">
        <div className="ipdi-promise-body">
          <div className="ipdi-promise-top">
            <div className="ipdi-promise-percent">100%</div>
            <h2 className="ipdi-promise-title">Placement Support</h2>

            <p className="ipdi-promise-value">
              Learn with experts, build live projects and get complete
              placement guidance until you're hired.
            </p>

            <div className="ipdi-promise-highlight-grid">
              <div className="ipdi-promise-highlight-item">
                <IconTarget />
                <span>Industry Mentorship</span>
              </div>
              <div className="ipdi-promise-highlight-item">
                <IconStar />
                <span>Resume & Interview Preprations</span>
              </div>
              <div className="ipdi-promise-highlight-item">
                <IconVideo />
                <span>Mock Interviews</span>
              </div>
              <div className="ipdi-promise-highlight-item">
                <IconCheck />
                <span>Job Assistance</span>
              </div>
            </div>
          </div>

          <div className="ipdi-promise-footer-row">
            <div className="ipdi-promise-audience-block">
              <div className="ipdi-promise-audience-label">Applies To</div>
              <div className="ipdi-promise-audience-row">All Enrolled Students</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Big horizontal hero card ----------------
   Uses NEW, UNIQUE class names prefixed "ipdi-big-": ipdi-big-title,
   ipdi-big-meta-row, ipdi-big-value-prop, ipdi-big-highlight-grid,
   ipdi-big-audience-cta-row, ipdi-big-cta-primary, etc.
   These do NOT share CSS with the small cards anymore — edit freely
   in the "BIG CARD CSS" section below without touching small cards.
------------------------------------------------------------------- */

function ProgramCardBig({ program }) {
  return (
    <div className="ipdi-card-big" id="programs">
      <div className="ipdi-big-media">
        <img src={program.image} alt={program.title} loading="lazy" />
        <div className="ipdi-big-eyebrow-badge">
          <IconBadge />
          {program.eyebrow}
        </div>
        {program.badge && (
          <div className="ipdi-big-media-badge-line">{program.badge}</div>
        )}
      </div>

      <div className="ipdi-big-body">
        {program.category && (
          <div className="ipdi-big-heading-eyebrow">
            <span className="ipdi-big-heading-dash"></span>
            {program.category}
          </div>
        )}

        <h2 className="ipdi-big-title">{program.title}</h2>

        <div className="ipdi-big-meta-row">
          <span className="ipdi-big-meta-chip">
            <IconClock />
            {program.meta[0]}
          </span>
          {program.meta[1] && (
            <span className="ipdi-big-meta-chip">
              {React.createElement(getMetaIcon(program.meta[1]))}
              {program.meta[1]}
            </span>
          )}
          {program.meta[2] && (
            <span className="ipdi-big-meta-chip">
              {React.createElement(getMetaIcon(program.meta[2]))}
              {program.meta[2]}
            </span>
          )}
          {program.nextBatch && (
            <span className="ipdi-big-meta-chip">
              <IconUser />
              Next Batch: {program.nextBatch}
            </span>
          )}
          {program.placementNote && (
            <span className="ipdi-big-meta-chip">
              <IconBadge />
              {program.placementNote}
            </span>
          )}
        </div>

        <p className="ipdi-big-value-prop">{program.valueProp}</p>

        <div className="ipdi-big-highlight-grid">
          {program.highlights.map((h, i) => {
            const Icon = highlightIcons[i % highlightIcons.length];
            return (
              <div className="ipdi-big-highlight-item" key={h}>
                <Icon />
                <span>{h}</span>
              </div>
            );
          })}
        </div>

        <div className="ipdi-big-divider" />

        <div className="ipdi-big-audience-cta-row">
          <div className="ipdi-big-audience-block">
            <div className="ipdi-big-audience-label">Built For</div>
            <div className="ipdi-big-audience-row">{program.audience.join(" | ")}</div>
          </div>

          <a className="ipdi-big-cta-primary" href={program.link}>
            {program.ctaLabel}
            <IconArrow />
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Row wrapper + heading + styles ---------------- */

export default function ProgramCardsRow2() {
  const [heroProgram, ...restPrograms] = programs;

  return (
    <div className="ipdi-programs-wrap" id="programs2">
      <style>{css}</style>

      <div className="ipdi-section-heading">
        <div className="ipdi-heading-eyebrow">
          <span className="ipdi-heading-dash"></span>
          What We Offer
        </div>
        <h2 className="ipdi-heading-title">
          Programs That <em>Meet Industry Demands</em>
        </h2>
      </div>

      <div className="ipdi-grid-context">
        <ProgramCardBig program={heroProgram} />

        <div className="ipdi-small-row">
          {restPrograms.map((p) => (
            <ProgramCard program={p} key={p.title} />
          ))}
          <ProgramCardPromise />
        </div>
      </div>
    </div>
  );
}

const css = `
.ipdi-programs-wrap{
  --void:#FFFFFF;
  --panel:#1A1613;
  --text:#F5F1E8;
  --muted:#A39C8E;
  --muted-dim:#6E675C;
  --accent:#C24A1E;
  --accent-light:#DE7042;
  --accent-dim:rgba(194,74,30,0.14);
  --hair:rgba(245,241,232,0.08);
  --hair-strong:rgba(245,241,232,0.15);
  background:var(--void);
  padding:60px 0 100px 0;
  font-family:'Inter',sans-serif;
}
.ipdi-programs-wrap *{box-sizing:border-box;}

/* ---- Section heading ---- */
.ipdi-section-heading{
  max-width:100%;
  margin:0 0 32px;
  padding:0 2.5rem;
  text-align:start;
}

.ipdi-heading-eyebrow{
  display:inline-flex;align-items:center;gap:8px;
  font-family:'space Mono',monospace;
  font-size:11px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;
  color:var(--accent);
  margin-bottom:12px;
}

.ipdi-heading-dash{
  width:22px;
  height:1px;
  background:var(--accent);
  display:inline-block;
}

.ipdi-heading-title{
  font-family:var(--serif);
  font-weight:400;
  font-size:clamp(28px, 4vw, 52px);
  text-align:start;
  line-height:1.2;
  color:var(--charcoal);
  margin:0 0 60px;
  letter-spacing:-0.01em;
}

.ipdi-heading-title em
{
  font-style:italic;
  font-weight:400;
  color:#c84b20;
}

/* ---- Outer stack: big hero card, then a row of small cards ---- */
.ipdi-grid-context{
  display:flex;
  flex-direction:column;
  gap:10px;
  max-width:100%;
  margin:0;
  padding:0 2.5rem;
}

/* =====================================================================
   SMALL CARD CSS  (ProgramCard only)
===================================================================== */

.ipdi-small-row{
  display:flex;
  gap:10px;
  align-items:stretch;
  flex-wrap:wrap;
  border-radius:0;
  overflow:visible;
  border-top:none;
}

.ipdi-small-row .ipdi-card{
  border-radius:0;
}

.ipdi-small-row .ipdi-card-promise{
  // border-radius:4px;
}

.ipdi-card{
  position:relative;
  width:382px;
  background:var(--panel);
  border:1px solid var(--hair-strong);
  border-radius:0;
  overflow:hidden;
  color:var(--text);
  box-shadow:none;
  flex-shrink:0;
  display:flex;
  flex-direction:column;
  transition:background 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease;
}

.ipdi-card::after{
  content:"";
  position:absolute;
  top:0; left:-75%;
  width:35%; height:100%;
  background:linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.12) 55%, rgba(255,255,255,0) 100%);
  transform:skewX(-15deg);
  pointer-events:none;
  z-index:6;
  opacity:0;
  transition:left 1.8s cubic-bezier(.4,0,.2,1), opacity 0.8s ease-in-out;
}

.ipdi-card:hover::after{ left:135%; opacity:1; }
.ipdi-card:hover{ background:var(--panel);
 border-color:#a92c05; 
 box-shadow:none; }

.explore-program-btn{ margin-top:12px; }

@media (min-width:769px){
  .ipdi-card{ width:auto; flex:1 1 0; min-width:0; }
}

.ipdi-card-image{
  position:relative;
  height:200px;
  overflow:hidden;
  background:radial-gradient(130% 150% at 15% 0%, #241E18 0%, #0D0B09 72%);
  transition:background 0.6s ease;
}

.ipdi-card-image img{
  position:absolute; inset:0; 
  width:100%; 
  height:100%;
  object-fit:cover;
  object-position:center;
  transform:scale(1);
  transition:transform 0.6s ease;
}

.ipdi-card-image::before{
  content:""; position:absolute; inset:0; background:var(--panel);
  opacity:0; z-index:1; transition:opacity 0.4s ease;
}

.ipdi-card-image::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(180deg, transparent 30%, rgba(13,11,9,0.94) 100%);
  transition:opacity 0.5s ease; z-index:1;
}

.ipdi-card:hover .ipdi-card-image img{ transform:scale(1.08); }
.ipdi-card:hover .ipdi-card-image::before{ opacity:0.55; }
.ipdi-card--light .ipdi-card-image::after{
  background:linear-gradient(180deg, transparent 55%, rgba(13,11,9,0.85) 100%);
}

.ipdi-card-body-wrap{
  position:relative;
  flex:1;
  display:flex;
  flex-direction:column;
}
.ipdi-card-body-wrap::before{
  content:"";
  position:absolute; top:0; left:0; right:0; bottom:0;
  background-image: none;
  opacity:0.35;
  pointer-events:none;
  -webkit-mask-image:linear-gradient(180deg, black, transparent 45%);
   mask-image:linear-gradient(180deg, black, transparent 45%);
}

.ipdi-card--light .ipdi-card-body-wrap::before{ background-image:none; }


.ipdi-eyebrow-badge{
  position:absolute; 
  top:12px;
  left:12px;
  display:inline-flex;
  align-items:center; 
  gap:5px;
  font-family:'JetBrains Mono',monospace;
  font-size:10px;
  font-weight:500;
  letter-spacing:0.08em;
  text-transform:uppercase;
  color:white;
  background:black;
  border:1px solid rgba(245,241,232,0.25);
  padding:5px 10px 5px 8px;
  border-radius:100px;
  white-space:nowrap; z-index:2;
}
.ipdi-eyebrow-badge svg{ width:11px; height:11px; }

.ipdi-card-body{
  position:relative;
  padding:20px 20px 20px 20px;
  display:flex;
  flex-direction:column;
  flex:1;
  justify-content:space-between;
}

.ipdi-title{
  font-family:'Playfair Display', Georgia, serif;
  font-weight:400; font-size:23px; line-height:1.18;
  margin:0 0 10px; letter-spacing:-0.005em; color:#FDFBF6;
}
.ipdi-card--light .ipdi-title{ color:#1A1613; }

.ipdi-meta-row{ display:flex; flex-wrap:wrap; align-items:center; gap:10px;

margin-bottom:20px;
}

.ipdi-meta-chip{
  font-family:'JetBrains Mono',monospace; font-size:12px; font-weight:600; color:#fff;
  display:flex; align-items:center; gap:5px;
}
  
.ipdi-meta-chip svg{ width:12px; height:12px; opacity:0.85; color:var(--accent-light); }
.ipdi-meta-dot{ width:3px; height:3px; border-radius:50%; background:rgba(245,241,232,0.4); flex-shrink:0; margin-top:-18px; }

.ipdi-meta-row--footer .ipdi-meta-chip--muted{
  font-weight:500; text-transform:uppercase; letter-spacing:0.05em;
  font-size:10px; font-family:'Space Mono',monospace;
}

.ipdi-meta-row--footer .ipdi-meta-chip--muted svg{ color:var(--accent-light); opacity:0.9; }
.ipdi-meta-dot--muted{ background:rgba(163,156,142,0.45) !important; }
.ipdi-card--light .ipdi-meta-chip--muted{ color:#3D362C; }
.ipdi-card--light .ipdi-meta-chip--muted svg{ color:#c84b20; }
.ipdi-card--light .ipdi-meta-dot--muted{ background:rgba(26,22,19,0.25) !important; }

.ipdi-value-prop{
  font-size:12px; 
  font-family:'Outfit',sans-serif; 
  line-height:1.55;
  color:#A39C8E;
   opacity:0.92; 
   font-weight:200;
  min-height:50px;
   transition:color 0.3s ease;
  margin-bottom:15px;
}

.ipdi-card:hover .ipdi-value-prop{ color:var(--text); opacity:1; }
.ipdi-card--light .ipdi-value-prop{ color:#6B6455; opacity:1; }
.ipdi-card--light:hover .ipdi-value-prop{ color:#1A1613; }

.ipdi-highlight-grid{
  display:grid;
  // grid-template-columns:1fr 1fr;
  gap:8px 10px;
  margin-bottom:25px;
}

.ipdi-highlight-item{
  position:relative; display:flex; align-items:center; gap:9px;
  width:100%; max-width:100%;
  background:rgba(245,241,232,0.03); border:1px solid var(--hair);
  padding:4px 10px; 
  transition:background 0.2s ease, border-color 0.2s ease;
}

.ipdi-highlight-item:hover{ background:rgba(194,74,32,0.07); }
.ipdi-highlight-item svg{ width:14px; height:14px; color:var(--accent-light); flex-shrink:0; }
 
.ipdi-highlight-item span{
  font-family:'Outfit',sans-serif; 
    // font-family:'Space Mono', monospace; 
  font-size:12px; 
  // font-weight:500; 
   font-weight:200;
  color:var(--muted);
  // line-height:1.3;
  line-height:1.55;
   white-space:normal; 
  // letter-spacing:0.01em;
      letter-spacing: 0.05em;
      //  min-height:50px;
  // transition:color 0.3s ease;

}

  


.ipdi-card:hover .ipdi-highlight-item{ background:rgba(12, 12, 10, 0.05); border-color:rgba(245,241,232,0.2); }

.ipdi-card:hover .ipdi-highlight-item span{ color:var(--text); }
.ipdi-card--light .ipdi-highlight-item{ background:rgba(26,22,19,0.03); border-color:rgba(26,22,19,0.1); }

.ipdi-card--light .ipdi-highlight-item span{ color:#6B6455; }

.ipdi-card--light:hover .ipdi-highlight-item{ background:rgba(200,75,32,0.06); border-color:rgba(200,75,32,0.25); }

.ipdi-card--light:hover .ipdi-highlight-item span{ color:#1A1613; }

.ipdi-audience-cta-row{
  display:flex; align-items:flex-end; justify-content:space-between; gap:12px;
  margin-top:12px;  border-top:1px solid var(--hair);
  transition:border-color 0.3s ease;
  padding-top:10px;
}
.ipdi-card:hover .ipdi-audience-cta-row{ border-top-color:rgba(245,241,232,0.15); }
.ipdi-card--light .ipdi-audience-cta-row{ border-top-color:rgba(26,22,19,0.12); }
.ipdi-card--light:hover .ipdi-audience-cta-row{ border-top-color:rgba(200,75,32,0.3); }

.ipdi-audience-block{ min-width:0; flex:1; }
.ipdi-audience-label{
  font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:500;
  letter-spacing:0.1em; text-transform:uppercase; color:var(--muted-dim);
  margin-bottom:8px; margin-top:12px; text-align:left; transition:color 0.3s ease;
}
.ipdi-card:hover .ipdi-audience-label{ color:var(--muted); }
.ipdi-card--light .ipdi-audience-label{ color:#8A8272; }
.ipdi-card--light:hover .ipdi-audience-label{ color:#6B6455; }

.ipdi-audience-row{
  display:block; white-space:normal; font-size:9.85px; line-height:1.2;
  color:#A6A39B; font-family:'Space Mono', monospace;
  // min-height:24px;
}
.ipdi-card--light .ipdi-audience-row{ color:rgba(26,22,19,0.75); }

.ipdi-cta-primary{
  display:flex;
   align-items:center;
   justify-content:center;
   gap:8px;
   width: 154px;
  // width:fit-content;
   flex-shrink:0;
  background:var(--accent); 
  color:#FDFBF6; 
  text-decoration:none;
  font-family:'Inter',sans-serif; 
  font-weight:500; font-size:13px;
  padding:8px 18px; border:1px solid transparent;
   cursor:pointer;
  position:relative; 
  overflow:hidden;
  transition:box-shadow 0.15s ease, background 0.15s ease, border-color 0.2s ease;
}

.ipdi-cta-primary:hover{ background:var(--accent-light); box-shadow:none; }

.ipdi-cta-primary svg{ width:14px; height:14px; }
.ipdi-card:hover .ipdi-cta-primary{ background:#FFFFFF; color:#c84b20; border-color:transparent; }
.ipdi-card:hover .ipdi-cta-primary:hover{ background:#F5F1E8; color:#c84b20; box-shadow:none; }

.ipdi-card--light .ipdi-cta-primary{
//  background:#1A1613; color:#F7F3EA; 
 background:#c84b20; color:#fff;
 }

.ipdi-card--light .ipdi-cta-primary:hover{ background:#c84b20; color:#fff; box-shadow:none; }
.ipdi-card--light:hover .ipdi-cta-primary{ background:#1A1613; color:#F7F3EA; border-color:transparent; }
.ipdi-card--light:hover .ipdi-cta-primary:hover{ background:#c84b20; color:#fff; }

.ipdi-card--light{ background:#f5f2ed; border-color:rgba(26,22,19,0.1); color:#1A1613; }
.ipdi-card--light:hover{ background:#FFFFFF; border-color:#c84b20; box-shadow:none; }

/* ======================================================================
   PROMISE CARD CSS  (ProgramCardPromise only)
   Fully independent from .ipdi-card / .ipdi-card--light. Edit here without
   affecting the other two small cards.
======================================================================== */

.ipdi-card-promise{
  position:relative;
  width:382px;
  background:var(--panel);
  border:1px solid var(--hair-strong);
  // border-radius:4px;
  overflow:hidden;
  color:var(--text);
  box-shadow:none;
  flex-shrink:0;
  display:flex;
  flex-direction:column;
  transition:background 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease;
}

.ipdi-card-promise::after{
  content:"";
  position:absolute;
  top:0; left:-75%;
  width:35%; height:100%;
  background:linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.12) 55%, rgba(255,255,255,0) 100%);
  transform:skewX(-15deg);
  pointer-events:none;
  z-index:6;
  opacity:0;
  transition:left 1.8s cubic-bezier(.4,0,.2,1), opacity 0.8s ease-in-out;
}

.ipdi-card-promise:hover::after{ left:135%; opacity:1; }
.ipdi-card-promise:hover{
  background:var(--panel);
  border-color:#a92c05;
  box-shadow:none;
}

@media (min-width:769px){
  .ipdi-card-promise{ width:auto; flex:1 1 0; min-width:0; }
}

.ipdi-promise-image{
  position:relative;
  height:200px;
  overflow:hidden;
  background:radial-gradient(130% 150% at 15% 0%, #241E18 0%, #0D0B09 72%);
  transition:background 0.6s ease;
}

.ipdi-promise-image img{
  position:absolute; inset:0;
  width:100%; height:100%;
  object-fit:cover;
  object-position:center;
  transform:scale(1);
  transition:transform 0.6s ease;
}

.ipdi-promise-image::before{
  content:""; position:absolute; inset:0; background:var(--panel);
  opacity:0; z-index:1; transition:opacity 0.4s ease;
}

.ipdi-promise-image::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(180deg, transparent 30%, rgba(13,11,9,0.94) 100%);
  transition:opacity 0.5s ease; z-index:1;
}

.ipdi-card-promise:hover .ipdi-promise-image img{ transform:scale(1.08); }
.ipdi-card-promise:hover .ipdi-promise-image::before{ opacity:0.55; }

.ipdi-promise-body-wrap{
  position:relative;
  display:flex;
  flex-direction:column;
  flex:1;
}

.ipdi-promise-badge{
  position:absolute;
  top:12px; left:12px;
  display:inline-flex;
  align-items:center;
  gap:5px;
  font-family:'JetBrains Mono',monospace;
  font-size:10px;
  font-weight:500;
  letter-spacing:0.08em;
  text-transform:uppercase;
  color:white;
  background:black;
  border:1px solid rgba(245,241,232,0.25);
  padding:5px 10px 5px 8px;
  border-radius:100px;
  white-space:nowrap;
  z-index:2;
}
.ipdi-promise-badge svg{ width:11px; height:11px; }

.ipdi-promise-body{
  position:relative;
  padding:20px 20px 20px 20px;
  display:flex;
  flex-direction:column;
  flex:1;
  justify-content:space-between;
  border-radius:0;
}

.ipdi-promise-top{
  display:flex;
  flex-direction:column;
}

.ipdi-promise-percent{
  font-family:"Playfair Display", Georgia, serif;
  font-size:35px;
  font-weight:500;
  line-height:1;
  color:var(--accent);
  margin-bottom:6px;
}

.ipdi-promise-title{
  font-family:'Playfair Display', Georgia, serif;
  font-weight:400; font-size:19px; line-height:1.18;
  margin:0 0 25px 0; 
  letter-spacing:-0.005em; color:#FDFBF6;

}

.ipdi-promise-value{
  font-size:12px; font-family:'Outfit',sans-serif; line-height:1.55;
  color:#A39C8E; opacity:0.92;
  font-weight:200;
  min-height:50px; transition:color 0.3s ease;
  margin-bottom:25px;
}
.ipdi-card-promise:hover .ipdi-promise-value{ color:var(--text); opacity:1; }

.ipdi-promise-highlight-grid{
  display:grid;
  // grid-template-columns:1fr 1fr;
  gap:8px 10px;
  // margin-bottom:25px;

}

.ipdi-promise-highlight-item{
  position:relative; display:flex; align-items:center; gap:9px;
  width:100%; max-width:100%;
  background:rgba(245,241,232,0.03); border:1px solid var(--hair);
  padding:6px 10px; transition:background 0.2s ease, border-color 0.2s ease;
}
.ipdi-promise-highlight-item:hover{ background:rgba(194,74,32,0.07); }
.ipdi-promise-highlight-item svg{ width:14px; height:14px; color:var(--accent-light); flex-shrink:0; }

.ipdi-promise-highlight-item span{
  font-family:'Outfit',sans-serif; font-size:11px; font-weight:400; color:var(--muted);
  line-height:1.3; white-space:normal; letter-spacing:0.01em;
  transition:color 0.3s ease;
}

.ipdi-card-promise:hover .ipdi-promise-highlight-item{
  background:rgba(245,241,232,0.05); border-color:rgba(245,241,232,0.2);
}
.ipdi-card-promise:hover .ipdi-promise-highlight-item span{ color:var(--text); }

.ipdi-promise-footer-row{
  display:flex; align-items:flex-end; 
  justify-content:space-between;
   gap:0;
   margin:0;
  // margin-top:12px; 
  border-top:1px solid var(--hair);
  transition:border-color 0.3s ease;
}
.ipdi-card-promise:hover .ipdi-promise-footer-row{ border-top-color:rgba(245,241,232,0.15); }

.ipdi-promise-audience-block{ min-width:0; flex:1; }
.ipdi-promise-audience-label{
  font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:500;
  letter-spacing:0.1em; text-transform:uppercase; color:var(--muted-dim);
  margin-bottom:8px; margin-top:19px; text-align:left; transition:color 0.3s ease;
}
.ipdi-card-promise:hover .ipdi-promise-audience-label{ color:var(--muted); }

.ipdi-promise-audience-row{
  display:block; white-space:normal; font-size:9.85px; line-height:1.5;
  color:#A6A39B; font-family:'Space Mono', monospace;
}

/* ======================================================================
   BIG CARD CSS  (ProgramCardBig only)
======================================================================== */

.ipdi-card-big{
  position:relative;
  display:flex;
  gap:0;
  background:var(--panel);
  // border-radius:4px;
  overflow:hidden;
  color:var(--text);
  box-shadow:none;
  transition:border-color 0.6s ease, box-shadow 0.6s ease;
}


.ipdi-card-big::after{
  content:"";
  position:absolute; top:0; left:-75%; width:35%; height:100%;
  background:linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.12) 55%, rgba(255,255,255,0) 100%);
  transform:skewX(-15deg);
  pointer-events:none; z-index:6; opacity:0;
  transition:left 1.8s cubic-bezier(.4,0,.2,1), opacity 0.8s ease-in-out;
}

.ipdi-card-big:hover::after{ left:135%; opacity:1; }
.ipdi-card-big:hover{ border-color:#a92c05; box-shadow:none; }

.ipdi-big-media{
  position:relative;
  flex:0 0 44%;
  min-height:440px;
  margin:0; margin-right:-1px;
  overflow:hidden;
  background:radial-gradient(130% 150% at 15% 0%, #241E18 0%, #0D0B09 72%);
}

.ipdi-big-media img{
  position:absolute; inset:0; width:100%; height:100%;
  object-fit:cover; object-position:center; transform:scale(1);
  transition:transform 0.6s ease;
}

.ipdi-big-media::before{
  content:""; position:absolute; inset:0; background:var(--panel);
  opacity:0; z-index:1; transition:opacity 0.4s ease;
}

.ipdi-big-media::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(180deg, rgba(13,11,9,0.05) 45%, rgba(13,11,9,0.55) 100%);
  z-index:1;
}
.ipdi-card-big:hover .ipdi-big-media img{ transform:scale(1.08); }
.ipdi-card-big:hover .ipdi-big-media::before{ opacity:0.55; }

.ipdi-big-eyebrow-badge{
  position:absolute; top:12px; left:12px;
  display:inline-flex; align-items:center; gap:5px;
  font-family:'JetBrains Mono',monospace;
  font-size:10px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase;
  color:white; background:black;
  border:1px solid rgba(245,241,232,0.25);
  padding:5px 10px 5px 8px; border-radius:100px;
  white-space:nowrap; z-index:2;
}
.ipdi-big-eyebrow-badge svg{ width:11px; height:11px; }

.ipdi-big-media-badge-line{
  position:absolute; top:46px; left:18px; z-index:2;
  display:inline-flex; align-items:center; gap:8px; width:fit-content;
  font-family:'Space Mono',monospace;
  font-size:11px; font-weight:500; letter-spacing:0.14em; text-transform:uppercase;
  color:var(--accent-light);
  text-shadow:0 1px 6px rgba(0,0,0,0.7);
  white-space:nowrap;
}

.ipdi-big-body{
  position:relative;
  flex:1;
  padding:40px 40px 0 40px;
  display:flex;
  flex-direction:column;
  justify-content:space-between;
  gap:0;          
}

.ipdi-big-heading-eyebrow{
  display:inline-flex;
  align-items:center; gap:8px;
  font-family:'space Mono',monospace;
  font-size:11px; 
  font-weight:500;
  letter-spacing:0.14em; 
  text-transform:uppercase;
  color:var(--accent);
}

.ipdi-big-heading-dash
{ width:22px;
 height:1px; background:var(--accent);
  display:inline-block;
  }

.ipdi-big-title{
  font-family:'Playfair Display', Georgia, serif;
  font-weight:400;
  font-size:34px;
  line-height:1.18;
  letter-spacing:-0.005em;
  color:#FDFBF6;
}

.ipdi-big-meta-row{ display:flex; flex-wrap:wrap; align-items:center;
 gap:10px;
 margin-bottom:20px;
 margin-top:5px;

 }
.ipdi-big-meta-chip{
   text-transform:uppercase;
    letter-spacing:0.09em;
  font-size:9.75px;
   font-family:'Space Mono',monospace;
  color:#fff;
   display:flex;
    align-items:center; gap:5px;
  // margin-bottom:25px;
  white-space:normal; 
  flex-wrap:wrap;
  line-height:1.5; 
}

.ipdi-big-meta-chip svg{ width:12px; height:12px; opacity:0.9; color:var(--accent-light); }
.ipdi-big-meta-dot{ width:3px; height:3px; border-radius:50%; background:rgba(163,156,142,0.45); flex-shrink:0; 
margin-bottom:23px;
}

.ipdi-big-value-prop{
  font-size:12.5px;
  font-family:'Outfit',sans-serif;
  line-height:1.55;
  color:#A39C8E;
  opacity:0.92;
  font-weight:200;
  max-width:620px;
  margin-bottom:45px;
  transition:color 0.3s ease;
}
.ipdi-card-big:hover .ipdi-big-value-prop{ color:var(--text); opacity:1; }

.ipdi-big-highlight-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px 10px;
  margin-bottom:42px;
}

.ipdi-big-highlight-item
{
  position:relative; display:flex; align-items:center; gap:9px;
  width:100%;
  // background:rgba(245,241,232,0.03);
  background:rgba(243, 149, 115, 0.2);
   border:1px solid var(--hair);
  padding:6px 10px; transition:background 0.2s ease, border-color 0.2s ease;
}

// .ipdi-big-highlight-item:hover
// { background:rgba(255, 68, 0, 0.66); }

.ipdi-big-highlight-item svg{ width:14px; height:14px; color:var(--accent-light); flex-shrink:0; }

.ipdi-big-highlight-item span{
  font-family:'Outfit',sans-serif; font-size:10.4px; font-weight:400; color:var(--muted);
  line-height:1.3; white-space:normal; letter-spacing:0.01em; transition:color 0.3s ease;
}

.ipdi-card-big:hover .ipdi-big-highlight-item
{
//  background:rgba(245,241,232,0.05);
//  border-color:rgba(245,241,232,0.2); }

.ipdi-card-big:hover .ipdi-big-highlight-item span{ color:var(--text); }

.ipdi-big-divider{
 border-top:1px solid var(--hair-strong);
  width:100%;
 }

.ipdi-big-audience-cta-row{
  margin-bottom:30px;
  margin-top:10px;
  display: flex;
    justify-content: flex-start;
}
.ipdi-big-audience-block{ min-width:0; flex:1;
gap:10px; }


.ipdi-big-audience-label{
  font-family:'JetBrains Mono',monospace; font-size:12px; font-weight:500;
  letter-spacing:0.1em; text-transform:uppercase; color:var(--muted-dim);
   transition:color 0.3s ease;
   margin-top:8px;
}

.ipdi-card-big:hover .ipdi-big-audience-label{ color:var(--muted); }
.ipdi-big-audience-row{
  display:block; 
  // white-space:normal; 
  flex-wrap:wrap;
  font-size:11px; 
  line-height:1.5; 
  color:#A6A39B;
  font-family:'Space Mono', monospace;
  margin-top:15px;
}

.ipdi-big-cta-primary{
  display:flex;
  align-items:center;
  justify-content:center; gap:8px;
  width:fit-content; flex-shrink:0;
  background:var(--accent); color:#FDFBF6; text-decoration:none;
  font-family:'Inter',sans-serif; font-weight:500; font-size:13px;
  padding:10px 30px; margin-top:15px;
  border:1px solid transparent; cursor:pointer;
  position:relative; overflow:hidden;
  transition:box-shadow 0.15s ease, background 0.15s ease, border-color 0.2s ease;
}

// .ipdi-big-cta-primary:hover{ background:var(--accent-light); box-shadow:none; }

.ipdi-big-cta-primary svg{ width:14px; height:14px; }
// .ipdi-card-big:hover .ipdi-big-cta-primary{ background:#FFFFFF; color:#c84b20; border-color:transparent; }
// .ipdi-card-big:hover .ipdi-big-cta-primary:hover{ background:#F5F1E8; color:#c84b20; box-shadow:none; }

/* ---- MOBILE: now you can tweak ONLY the small cards ... ---- */
@media (max-width:768px){
  .ipdi-section-heading{ padding:0 16px; margin-bottom:24px; 
  
         align-items:center;
         text-align:center;

  }

.ipdi-heading-title{   align-items:center;
         text-align:center;   }

  .ipdi-grid-context{ padding:0 16px; gap:30px; }

  .ipdi-small-row{
    flex-direction:column; gap:30px; border:none; border-radius:0;
    box-shadow:none; overflow:visible;
  }

  .ipdi-small-row .ipdi-card{ border-radius:0; box-shadow:none; }

  .ipdi-card{ width:100%; max-width:100%; box-shadow:none; }
  .ipdi-audience-row{ margin-bottom:0; }

  .ipdi-audience-cta-row{ flex-direction:column; align-items:stretch; }
  .ipdi-cta-primary{ width:100%; margin-top:5px; }
}

/* ---- MOBILE: ONLY the promise card, independently ---- */
@media (max-width:768px){
  .ipdi-small-row .ipdi-card-promise{
    // border-radius:4px;
    box-shadow:none;
  }
  .ipdi-card-promise{ width:100%; max-width:100%; box-shadow:none; }
  .ipdi-promise-audience-row{ margin-bottom:15px; }
  .ipdi-promise-footer-row{ flex-direction:column; align-items:stretch; }
}

/* ---- MOBILE: ... and ONLY the big hero card, independently ----
   Padding/media-height here are matched to the small cards
   (.ipdi-card-body / .ipdi-card-image) so the hero card's mobile
   alignment lines up with the two cards below it. ---- */
@media (max-width:768px){
  .ipdi-card-big{ 
  flex-direction:column;
  //  border-radius:4px;
    box-shadow:none; }
  .ipdi-big-media{ flex:none; min-height:200px; }
  .ipdi-big-body{ padding:20px 20px 0 20px; }
  .ipdi-big-title{ font-size:24px; }
  .ipdi-big-audience-cta-row{ flex-direction:column; align-items:stretch; }
  .ipdi-big-cta-primary{ width:100%; margin-top:5px; }
  .ipdi-big-meta-row{margin-top:5px;}
  .ipdi-promise-body
  {
  padding: 20px 20px 15px 20px;
  }

  /* NEW: 2 equal boxes per row, mobile only */
  .ipdi-big-highlight-grid{
    display:grid;
    grid-template-columns:none;
    gap:8px 10px;
  }
  .ipdi-big-highlight-item{
    width:100%;
    max-width:100%;
  }
  .ipdi-big-highlight-item span{
    white-space:normal;
  }

  .ipdi-big-title{
  margin-bottom:20px;
  }

.ipdi-big-audience-row{
 margin-bottom:10px;
}

}   

@media (min-width:1600px){
  .ipdi-section-heading,
  .ipdi-grid-context{
    max-width:1400px;
    margin-left:auto;
    margin-right:auto;
  }
}

`;
